import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RamalhoLogo } from "@/components/RamalhoLogo";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Bem-vindo, " + username);
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao entrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 md:p-12 lg:p-16 bg-[#F9F9F6]">
        <RamalhoLogo />
        <div className="max-w-md w-full mx-auto lg:mx-0">
          <span className="overline text-[#52796F]">Acesso restrito</span>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#1A1A1A] tracking-tight mt-3">
            Bem-vindo de volta.
          </h1>
          <p className="text-sm text-[#4A4A4A] mt-3">
            Entre com suas credenciais de administrador para gerenciar as usinas e os clientes.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                Usuário
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Raphael"
                required
                autoComplete="username"
                className="h-11 rounded-md border-[#E5E5DF] bg-white focus-visible:ring-[#1B4332]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                Senha
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="h-11 rounded-md border-[#E5E5DF] bg-white focus-visible:ring-[#1B4332]"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#1B4332] text-white hover:bg-[#122d22]"
            >
              {loading ? "Entrando..." : (
                <>
                  Entrar
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
        <p className="text-xs text-[#4A4A4A] mt-8">
          © {new Date().getFullYear()} Ramalho Gestão Energética
        </p>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative overflow-hidden grain-overlay">
        <img
          src="https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Usina solar em solo"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B4332]/70 via-[#1B4332]/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="overline">A energia que você produz</div>
          <div className="font-display text-4xl mt-3 leading-tight max-w-md">
            Cada watt-hora com clareza. Cada crédito com propósito.
          </div>
        </div>
      </div>
    </div>
  );
}
